import React from 'react';
import {
  Accordion,
  AccordionSectionAction,
  Card
} from '@codametrix/ui-components';
import classnames from 'classnames';
import { cmxTypeguards, commonEnums, dragManager } from '@codametrix/ui-common';
import { Lookup, shouldShowResults } from '@codametrix/ui-components';
import { canDropOnto, CODE_ORDER } from './can-drop';
import { isAValidUnits, parseUnits } from './transaction-charge.utils';
import { CHARGE_TYPE_LABEL } from '../../code-bench-panel/invoice-meta/invoice-meta-info';
import ErrorSubmission from '../../code-bench-panel/error-submission';

import { TransactionProps, TransactionState } from './transaction.types';
import { CMXIcon } from '@codametrix/ui-components';
import TimeBasedCodePopover from './time-based-code-popover';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  PopoverBody,
  ChakraProvider,
  Portal
} from '@chakra-ui/react';
import { transactionAccordianBuilder } from './transaction-accordion';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import theme from '../../../theme';
import './styles/index.scss';
const { ChargeSessionCodeProvenance } = commonEnums;

const draggedCodesManager = dragManager.load('codes');

const sharedCodePredicate = (code: CMxCommonApp.ChargeSessionCode) => {
  //@ts-ignore
  return code.shared === true;
};

const notSharedCodePredicate = (code: CMxCommonApp.ChargeSessionCode) => {
  //@ts-ignore
  return !(code.shared === true);
};

/**
 * Method that get data from codable object
 * @param element
 * @param codable
 * @returns
 */
const getDataItem = (
  element: HTMLElement,
  codable: CMxCommonApp.ChargeSession | CMxCommonApp.CodableTransaction
) => {
  const parent = element.closest('.transaction-header');
  if (parent) {
    const codeId = parent.attributes.getNamedItem('data-code');
    const codeType = parent.attributes.getNamedItem('data-code-type');

    if (codeId && codeType) {
      return (
        codable.codes.find((code) => {
          if (!cmxTypeguards.isChargeSessionCode(code)) return false;
          const sessionCode = code as CMxCommonApp.ChargeSessionCode;
          return (
            `${sessionCode.code}` === `${codeId.value}` &&
            sessionCode.codeType === `${codeType.value}`
          );
        }) ?? null
      );
    }
  }
  return null;
};

/**
 * Class React component for render a transaction
 */
class Transaction extends React.Component<TransactionProps, TransactionState> {
  static displayName = 'Transaction';
  static defaultProps = {
    autoFocus: false,
    searchResults: [],
    editable: true,
    panelActivations: {},
    codeEdits: {},
    debounceTimeout: 200,
    canDropOnto: canDropOnto,
    onChargeSessionAction: (
      transactionAction: CMxCommonApp.TransactionAction
    ) => {},
    onTransactionSharedDiagnosisChange: (
      changeDetail: CMxCommonApp.TransactionSharedCodeChange
    ) => {},
    handleCodeChange: (change: CMxCommonApp.CodeChange) => {},
    handleCodeRemove: (change: CMxCommonApp.CodeChange) => {},
    handleCodeSearchDrop: (change: CMxCommonApp.CodeChange) => {},
    onCodeSearch: (codeQuery: CMxCommonApp.CodeQuery) => {},
    onCodeSearchSelection: (searchSelection: CMxCommonApp.CodeInfo) => {},
    onFeatureSelection: (feature: CMxCommonApp.ChargeSessionSelection) => {},
    onEdit: (chargeSession: CMxCommonApp.ChargeSession) => {},
    handleTimeBasedCodeAdd: (
      change: CMxCommonApp.CodeChange,
      time: string,
      age: string
    ) => {},
    handleTimeBasedCodeUpdate: (
      change: CMxCommonApp.CodeChange,
      time: string,
      age: string
    ) => {}
  };

  lookupRef: React.RefObject<Lookup>;
  iconRef: React.RefObject<HTMLButtonElement>;
  currentUnit: number;

  constructor(props: TransactionProps) {
    super(props);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleUnitsClick = this.handleUnitsClick.bind(this);
    this.handleCodeDelete = this.handleCodeDelete.bind(this);
    this.onSearch = this.onSearch.bind(this);
    this.onSearchSelection = this.onSearchSelection.bind(this);
    this.onDropHandler = this.onDropHandler.bind(this);
    this.onDragOver = this.onDragOver.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.onAccordionAction = this.onAccordionAction.bind(this);
    this.handleCodeChecked = this.handleCodeChecked.bind(this);
    this.handleEnableTechPro = this.handleEnableTechPro.bind(this);
    this.handleDisableTechPro = this.handleDisableTechPro.bind(this);
    this.isTimeBasedCode = this.isTimeBasedCode.bind(this);
    this.timeBasedCodePopup = this.timeBasedCodePopup.bind(this);

    this.lookupRef = React.createRef();
    this.iconRef = React.createRef();
    this.state = { dragHover: false, clickTriggered: false };
  }

  // logic to show the timebased popover after page load
  componentDidUpdate(prevProps) {
    const prevProcedure = prevProps.codableTransaction?.procedure;
    const newProcedure = this.props.codableTransaction?.procedure;

    if (prevProcedure?.procedure_code !== newProcedure?.procedure_code) {
      this.setState({ clickTriggered: false });
    }
    // This code opens the popover if the time code is required.
    // BSP customer asked for that to not be the default, but product wants it to be a configurable option.
    // Keeping this code so we can use it when we make it configurable.
    /* if (newProcedure) {
      if (
        newProcedure?.time_based &&
        newProcedure?.add_on_code_available &&
        !newProcedure.total_time &&
        !this.state.clickTriggered
      ) {
        this.iconRef.current.click();
        this.setState({ clickTriggered: true }); 
      }
    } */
  }

  /**
   * Event for drag over
   * @param ev
   */
  onDragOver(ev: React.DragEvent) {
    const data = draggedCodesManager.get();
    const searchResult = cmxTypeguards.isSearchResult(data);

    if (searchResult) {
      const { codableTransaction, chargeSession } = this.props;
      const hasCPT = (codableTransaction || chargeSession).codes
        .filter(cmxTypeguards.isChargeSessionCode)
        .some((sessionCode: CMxCommonApp.ChargeSessionCode) => {
          return (
            sessionCode.provenance.toUpperCase() ===
            ChargeSessionCodeProvenance.CPT.toUpperCase()
          );
        });

      const allowed =
        data.codeType.toUpperCase() === ChargeSessionCodeProvenance.CPT
          ? !hasCPT
          : true;
      if (allowed) {
        ev.dataTransfer.dropEffect = 'copy';
        ev.preventDefault();
        if (!this.state.dragHover) {
          this.setState({ dragHover: true });
        }
      }
    }
  }

  /**
   * Event for drag leaves
   * @param ev
   */
  onDragLeave(ev: React.DragEvent) {
    if (this.state.dragHover) {
      this.setState({ dragHover: false });
    }
    ev.dataTransfer.dropEffect = 'none';
  }

  /**
   * Event for drag drops
   * @param ev
   */
  onDrop(ev: React.DragEvent) {
    const searchResult = draggedCodesManager.parseEvent(ev);
    draggedCodesManager.clear();

    const { codableTransaction } = this.props;
    if (codableTransaction) {
      const codes: CMxCommonApp.ChargeSessionCode[] = codableTransaction.codes
        .filter((code): code is CMxCommonApp.ChargeSessionCode => cmxTypeguards.isChargeSessionCode(code))
        .filter((sessionCode) => {
          return (
            sessionCode.codeType.toUpperCase() ===
            searchResult.codeType.toUpperCase()
          );
        });
      const result = {
        ...searchResult,
        context: {
          invoice: { ...this.props.invoice },
          transaction: { ...this.props.codableTransaction },
          chargeSession: { ...this.props.chargeSession },
          processInstance: { ...this.props.processInstance }
        }
      };
      codes.push(result);
      this.props.onCodeSearchSelection({
        ...searchResult,
        context: {
          chargeSession: { ...this.props.chargeSession }
        }
      });

      this.setState({ dragHover: false });
      if (this.lookupRef.current) {
        this.lookupRef.current.reset();
        this.lookupRef.current.focus();
      }
    }
  }

  /**
   * Event for handle drag drop
   * @param ev
   */
  onDropHandler(
    dropAction: CMxCommonApp.DroppedAction<CMxCommonApp.ChargeSessionCode>
  ) {
    const { chargeSession, codableTransaction } = this.props;
    const { dropCandidate, dropTarget } = dropAction;
    const sameCodeType =
      `${dropCandidate.codeType}`.toUpperCase() ===
      `${dropTarget.codeType}`.toUpperCase();

    const matchCodes = (sessionCode: CMxCommonApp.ChargeSessionCode) => {
      const matchType =
        `${sessionCode.codeType}`.toUpperCase() ===
        `${dropCandidate.codeType}`.toUpperCase();
      const matchProvenance =
        `${sessionCode.provenance}`.toUpperCase() ===
        `${dropCandidate.codeType}`.toUpperCase();
      if (matchProvenance || matchType) {
        return sessionCode.code !== dropCandidate.code;
      }
      return false;
    };
    // remove the code from our code list
    let codes: CMxCommonApp.ChargeSessionCode[] = (
      codableTransaction || chargeSession
    ).codes
      .filter(matchCodes)
      .filter(cmxTypeguards.isChargeSessionCode);

    const searchResult = cmxTypeguards.isSearchResult(dropCandidate);
    let dropCode = dropCandidate;

    if (searchResult) {
      dropCode = {
        code: dropCandidate.code,
        codeType: dropCandidate.codeType,
        description: dropCandidate.description
      } as CMxCommonApp.ChargeSessionCode;

      let targetPosition = codes.findIndex(
        (sessionCode) => sessionCode.code === dropTarget.code
      );
      if (dropAction.position === 'after') {
        targetPosition++;
      }
      codes.splice(targetPosition, 0, dropCode);

      if (this.lookupRef.current) {
        this.lookupRef.current.reset();
        this.lookupRef.current.focus();
      }
    } else {
      // if the codes are of the same code type
      // then a drop is just a reordering of the same codes
      // if a drop is on a different code type, then we need
      // to do something different.
      if (sameCodeType) {
        let targetCode = dropTarget.code;

        if (cmxTypeguards.isZippedCodeBlock(dropCode)) {
          const zippedDropCode = dropCode as unknown as CMxCommonApp.ZippedCodeBlock;
          const blockSet = new Set(zippedDropCode.codes.map((code) => code.code));
          const filteredArray = [...codes].filter(
            (code) => !blockSet.has(code.code)
          );

          let seekPosition = filteredArray.findIndex(
            (sessionCode) => sessionCode.code === dropTarget.code
          );

          if (dropAction.position === 'after') {
            seekPosition++;
          }

          filteredArray.splice(seekPosition, 0, ...zippedDropCode.codes);
          codes = filteredArray;

          // capturing the drag complete for Datadog Action
          // @ts-ignore
          window.DD_RUM.addAction('Drag (shared row moved)', {});
        } else {
          // if this is a shared code block then we need to rewind to the
          // front of the shared codes
          let codeIncrement = 1;

          if (cmxTypeguards.isZippedCodeBlock(dropTarget)) {
            const zippedDropTarget = dropTarget as unknown as CMxCommonApp.ZippedCodeBlock;
            const [firstCode] = zippedDropTarget.codes;
            targetCode = firstCode.code;
            codeIncrement = zippedDropTarget.codes.length;
          }

          let targetPosition = codes.findIndex(
            (sessionCode) => sessionCode.code === targetCode
          );
          if (dropAction.position === 'after') {
            targetPosition += codeIncrement;
          }
          codes.splice(targetPosition, 0, dropCandidate);
        }

        // capturing the drag complete for Datadog Action
        // @ts-ignore
        window.DD_RUM.addAction('Drag (any code row moved)', {});
      } else {
        if (cmxTypeguards.isZippedCodeBlock(dropCandidate)) {
          const zippedDropCandidate = dropCandidate as unknown as CMxCommonApp.ZippedCodeBlock;
          const blockSet = new Set(
            zippedDropCandidate.codes.map((code) => code.code)
          );
          const filteredArray = [...codes].filter(
            (code) => !blockSet.has(code.code)
          );

          let seekPosition = filteredArray.findIndex(
            (sessionCode) => sessionCode.code === dropTarget.code
          );

          if (dropAction.position === 'after') {
            seekPosition++;
          }

          filteredArray.splice(seekPosition, 0, ...zippedDropCandidate.codes);
          codes = filteredArray;

          // capturing the drag complete for Datadog Action
          // @ts-ignore
          window.DD_RUM.addAction('Drag (shared row moved to top)', {});
        } else {
          // there is a defined visual order of codes
          // PROCEDURES, MODIFIERS, DX
          // if this changes, then the order will need to be updated
          const dropIndex = CODE_ORDER.get(`${dropCandidate.codeType}`);
          const dropTargetIndex = CODE_ORDER.get(`${dropTarget.codeType}`);
          if (dropIndex < dropTargetIndex) {
            codes.push(dropCandidate);
          } else {
            codes.unshift(dropCandidate);
          }

          // capturing the drag complete for Datadog Action
          // @ts-ignore
          window.DD_RUM.addAction('Drag (invoice code row move to top)', {});
        }
      }
    }

    const changeDetail = {
      codes: codes,
      context: {
        chargeSession: { ...this.props.chargeSession },
        invoice: { ...this.props.invoice },
        transaction: { ...this.props.codableTransaction },
        processInstance: { ...this.props.processInstance }
      }
    };
    this.props.handleCodeSearchDrop(changeDetail);
    this.setState({ dragHover: false });
  }

  /**
   * Event launched when a search is performed
   * @param ev
   */
  onSearch(codeQuery: CMxCommonApp.CodeQuery) {
    this.props.onCodeSearch({
      ...codeQuery,
      context: {
        transaction: { ...this.props.codableTransaction },
        invoice: { ...this.props.invoice },
        chargeSession: { ...this.props.chargeSession },
        processInstance: { ...this.props.processInstance }
      }
    });
  }

  /**
   * Event launched when a search selection is pressed
   * @param ev
   */
  onSearchSelection(searchResult: Accelerate.CodeActionPayload) {
    const present = this.props.codableTransaction.codes.findIndex(
      (code: CMxCommonApp.ChargeSessionCode) =>
        code.code === searchResult.codeInfo.code
    );
    const searchSelection: Accelerate.CodeActionPayload = {
      codeInfo: searchResult.codeInfo,
      context: {
        defaultPrevented: present !== -1,
        invoice: { ...this.props.invoice },
        transaction: { ...this.props.codableTransaction },
        chargeSession: { ...this.props.chargeSession },
        processInstance: { ...this.props.processInstance }
      }
    };
    this.props.onCodeSearchSelection(searchSelection);
  }

  /**
   * Event launched when units input is clicked
   * @param ev
   */
  handleUnitsClick(ev: React.MouseEvent<HTMLInputElement>) {
    ev.stopPropagation();
    if (this.props.editable) {
      const element = ev.target as HTMLElement;
      if (element instanceof HTMLInputElement && element.type === 'text') {
        element.select();
      }
    }
  }

  /**
   * Event launched when a code is deleted
   * @param ev
   */
  handleCodeDelete(ev: React.MouseEvent<HTMLInputElement>) {
    ev.preventDefault();
    ev.stopPropagation();
    if (this.props.editable) {
      const { chargeSession, codableTransaction } = this.props;
      const element = ev.target as HTMLElement;
      const dataItem = getDataItem(
        element,
        codableTransaction || chargeSession
      ) as CMxCommonApp.ChargeSessionCode;
      const changeDetail: CMxCommonApp.CodeChange = {
        codes: [dataItem],
        context: {
          chargeSession: { ...this.props.chargeSession },
          transaction: { ...this.props.codableTransaction },
          invoice: { ...this.props.invoice },
          processInstance: { ...this.props.processInstance }
        }
      };
      this.props.handleCodeRemove(changeDetail);
      if (this.lookupRef.current) {
        this.lookupRef.current.focus();
      }
    }
  }

  /**
   * Event launched when a shared code is toggled.
   * @param ev
   */
  handleCodeChecked(ev: React.ChangeEvent<HTMLInputElement>) {
    ev.stopPropagation();
    const { codableTransaction } = this.props;
    const action = ev.target.checked ? 'ADD' : 'REMOVE';
    let diagnoses = [];

    if (action === 'REMOVE') {
      diagnoses = [
        {
          diagnosis_code: ev.target.name,
          shared: true
        }
      ];
    } else if (action === 'ADD') {
      const codes = this.props.codableTransaction.codes
        .filter((code): code is CMxCommonApp.ChargeSessionCode => cmxTypeguards.isChargeSessionCode(code))
        .filter((code) => code.codeType === 'diagnosis');
      diagnoses = codes.map((code, idx) => {
        return {
          diagnosis_code: code.code,
          sequence_number: idx + 1,
          //@ts-ignore
          shared: code.shared,
          //@ts-ignore
          active: code.code === ev.target.name ? true : code.active
        };
      });
    }
    const payload = {
      action,
      codeUpdate: {
        transactionGuid: codableTransaction.guid,
        processInstance: this.props.processInstance,
        serviceLine: this.props.processInstance.serviceLine,
        diagnoses
      }
    };
    this.props.onTransactionSharedDiagnosisChange(payload);
  }

  /**
   * Event launched when a key is pressed on input tag
   * @param ev
   */
  handleKeyPress(ev: React.KeyboardEvent<HTMLInputElement>) {
    ev.stopPropagation();
    if (this.props.editable) {
      const { codableTransaction } = this.props;

      const element = ev.target as HTMLElement;
      const codeUnitsInput =
        element?.closest<HTMLInputElement>('.code-units-input');

      const dataItem = getDataItem(
        element,
        codableTransaction
      ) as CMxCommonApp.ChargeSessionCode;
      if (codeUnitsInput && isAValidUnits(codeUnitsInput.value)) {
        this.currentUnit = parseUnits(parseInt(codeUnitsInput.value));
        const code = {
          ...dataItem,
          units: this.currentUnit
        } as CMxCommonApp.ChargeSessionCode;

        const changeDetail: CMxCommonApp.CodeChange = {
          codes: [code],
          context: {
            chargeSession: { ...this.props.chargeSession },
            transaction: { ...this.props.codableTransaction },
            invoice: { ...this.props.invoice },
            processInstance: { ...this.props.processInstance }
          }
        };

        this.props.handleCodeChange(changeDetail);
      } else {
        this.currentUnit = dataItem?.units ? Number(dataItem?.units) : null;
      }
    }
  }

  /**
   * Manage onBlur event for valid units
   * @param ev
   */
  handleBlurEvent = (ev: React.FocusEvent<HTMLInputElement>) => {
    ev.stopPropagation();
    if (this.props.editable) {
      const element = ev.target as HTMLElement;
      const codeUnitsInput =
        element?.closest<HTMLInputElement>('.code-units-input');

      if (codeUnitsInput && this.currentUnit) {
        codeUnitsInput.value = this.currentUnit.toString();
      }
    }
  };

  /**
   * Event launched when a transaction is deleted
   * @param event
   */
  onDelete(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    this.props.onChargeSessionAction({
      action: 'DELETE_TRANSACTION',
      chargeSession: this.props.chargeSession,
      meta: {
        invoice: this.props.invoice,
        transaction: { ...this.props.codableTransaction }
      }
    });
  }

  /**
   * Event launched by the accordion
   * @param action
   * @param item
   */
  onAccordionAction(action: any, item: any) {
    const chargeSessionCode = item.data as CMxCommonApp.ChargeSessionCode;
    const opening = action === AccordionSectionAction.OPEN;

    const selection: CMxCommonApp.ChargeSessionSelection = {
      chargeSessionCode,
      evidence: chargeSessionCode.evidence,
      selectionState: {
        active: false,
        hover: false,
        visible: opening
      }
    };
    this.props.onFeatureSelection(selection);
  }

  /**
   * Method used for enable a procedure with tech/pro charge
   * @param techTransactionCodable
   */
  handleEnableTechPro(
    techTransactionCodable: CMxCommonApp.TechCodableTransaction
  ) {
    if (this.props.editable) {
      techTransactionCodable.invoice = this.props.invoice;
      this.props.handleEnableTechPro(techTransactionCodable);
    }
  }

  /**
   * Method used for disable a procedure with tech/pro charge
   * @param techTransactionCodable
   */
  handleDisableTechPro(
    techTransactionCodable: CMxCommonApp.TechCodableTransaction
  ) {
    if (this.props.editable) {
      techTransactionCodable.invoice = this.props.invoice;
      this.props.handleDisableTechPro(techTransactionCodable);
    }
  }

  isTimeBasedCode = () => {
    return this.props.codableTransaction?.procedure?.time_based;
  };

  handleTimeBasedCodeAdd = (
    ev: React.MouseEvent<HTMLElement>,
    time: string,
    age: string
  ) => {
    ev.preventDefault();
    ev.stopPropagation();
    const { chargeSession, codableTransaction } = this.props;
    const element = ev.target as HTMLElement;
    const dataItem = getDataItem(
      element,
      codableTransaction || chargeSession
    ) as CMxCommonApp.ChargeSessionCode;
    const changeDetail: CMxCommonApp.CodeChange = {
      codes: [dataItem],
      context: {
        chargeSession: { ...this.props.chargeSession },
        transaction: { ...this.props.codableTransaction },
        invoice: { ...this.props.invoice },
        processInstance: { ...this.props.processInstance }
      }
    };
    this.props.handleTimeBasedCodeAdd(changeDetail, time, age);
    if (this.lookupRef.current) {
      this.lookupRef.current.focus();
    }
  };

  handleTimeBasedCodeUpdate = (
    ev: React.MouseEvent<HTMLInputElement>,
    time: string,
    age: string
  ) => {
    ev.preventDefault();
    ev.stopPropagation();
    const { chargeSession, codableTransaction } = this.props;
    const element = ev.target as HTMLElement;
    const dataItem = getDataItem(
      element,
      codableTransaction || chargeSession
    ) as CMxCommonApp.ChargeSessionCode;
    const changeDetail: CMxCommonApp.CodeChange = {
      codes: [dataItem],
      context: {
        chargeSession: { ...this.props.chargeSession },
        transaction: { ...this.props.codableTransaction },
        invoice: { ...this.props.invoice },
        processInstance: { ...this.props.processInstance }
      }
    };
    this.props.handleTimeBasedCodeUpdate(changeDetail, time, age);
    if (this.lookupRef.current) {
      this.lookupRef.current.focus();
    }
  };

  isClockIconDisabled = () => {
    const procedure = this.props.codableTransaction?.procedure;
    const invoice = this.props.invoice;
    return (
      procedure &&
      invoice?.transactions?.some(
        (transaction) =>
          transaction?.procedure?.add_on_code_id ===
          procedure?.additionalProperties['id']
      )
    );
  };

  //handle this manually so we can unmount it
  handlePopoverClose = (newState: boolean) => {
    this.setState({ clickTriggered: newState });
  };

  timeBasedCodePopup = () => {
    const popoverState = this.state.clickTriggered;

    const handlePopoverOpen = (ev: React.MouseEvent<HTMLElement>) => {
      // Prevents the default behavior of the click event
      ev.stopPropagation();

      // Toggle the state when the user clicks to open the popover manually
      this.setState({ clickTriggered: !this.state.clickTriggered });
    };

    return (
      <ChakraProvider theme={theme}>
        <div
          className={
            this.props.codableTransaction?.procedure?.time_based &&
            this.props.codableTransaction?.procedure?.total_time
              ? 'clock-time-section'
              : 'clock-section'
          }>
          <Popover isOpen={popoverState} placement="bottom-end" variant="time">

              {/* @ts-ignore */}
            <PopoverTrigger>
              <Button
             data-testid="time-based-code-open-btn"
                ref={this.iconRef}
                aria-label=""
                size="xs"
                variant="clockv1"
                disabled={!this.props.editable || this.isClockIconDisabled()}
                onClick={handlePopoverOpen}>
                <CMXIcon
                  svgIconName="BiomeClock"
                  svgIconSize="20"
                  svgIconClass={
                    this.props.codableTransaction?.procedure?.time_based &&
                    this.props.codableTransaction?.procedure?.total_time
                      ? 'grey-clock'
                      : 'color-clock'
                  }
                />
                {this.props.codableTransaction?.procedure?.time_based &&
                  this.props.codableTransaction?.procedure?.total_time && (
                    <div className="total-time">
                      {this.props.codableTransaction.procedure.total_time}
                    </div>
                  )}
              </Button>
            </PopoverTrigger>
            <Portal>
              {popoverState && (
                <PopoverContent>
                  <PopoverBody>
                    <TimeBasedCodePopover
                      codableTransaction={this.props.codableTransaction}
                      handleTimeBasedCodeAdd={this.handleTimeBasedCodeAdd}
                      handleTimeBasedCodeUpdate={this.handleTimeBasedCodeUpdate}
                      processInstance={this.props.processInstance}
                      popoverState={popoverState}
                      updatePopoverState={this.handlePopoverClose}
                    />
                  </PopoverBody>
                </PopoverContent>
              )}
            </Portal>
          </Popover>
        </div>
      </ChakraProvider>
    );
  };

  render() {
    const {
      chargeSession,
      codableTransaction,
      lineNumber,
      editable,
      invoice,
      onCodeSearch,
      handleCodeChange,
      handleCodeSearchDrop,
      handleCodeRemove,
      canDropOnto,
      searchResults,
      onCodeSearchSelection,
      onFeatureSelection,
      onChargeSessionAction,
      onEdit,
      autoFocus,
      panelActivations,
      processInstance,
      transaction,
      debounceTimeout,
      codeEdits,
      onTransactionSharedDiagnosisChange,
      ...rest
    } = this.props;

    const txnType = transaction?.type ?? 'unknown txn type';

    const styleClasses: any = [
      'charge-session-information',
      `charge-session-${txnType.toLowerCase()}`,
      {
        'charge-session-editable': editable,
        'charge-session-uneditable': !editable,
        'charge-session-drop-zone': this.state.dragHover
      }
    ];

    const boundAccordionBuilder = transactionAccordianBuilder.bind(
      null,
      this.handleKeyPress,
      this.handleBlurEvent,
      this.handleUnitsClick,
      this.handleCodeDelete,
      this.handleCodeChecked,
      this.handleDisableTechPro,
      this.handleEnableTechPro,
      onFeatureSelection,
      this.isTimeBasedCode,
      this.timeBasedCodePopup,
      panelActivations,
      invoice,
      codeEdits
    );

    const assuranceErrorMessage = codableTransaction?.procedure?.errorMessages
      ? codableTransaction?.procedure?.errorMessages
      : [];

    // removing procedures with provenance TECH (derive a copy — props may be frozen)
    const codableCodesForRender =
      codableTransaction?.hasOwnProperty('codes') &&
      Array.isArray(codableTransaction.codes)
        ? codableTransaction.codes
            .filter((code): code is CMxCommonApp.ChargeSessionCode =>
              cmxTypeguards.isChargeSessionCode(code)
            )
            .filter(
              (code) =>
                !(
                  code.codeType === 'procedure' &&
                  code.provenance === 'TECH'
                )
            )
        : codableTransaction?.codes;

    const sharedCodes = invoice?.type === commonEnums.InvoiceTypes.SHARED_CODES;

    const boundAccordionBuilderHandler = (
      code: CMxCommonApp.ChargeSessionCode | CMxCommonApp.ZippedCodeBlock,
      index: number
    ) => {
      return boundAccordionBuilder(
        code,
        index,
        codableTransaction,
        assuranceErrorMessage
      );
    };

    let items = [];
    let chargeMeta: any = false;

    if (codableTransaction) {
      let codes = JSON.parse(JSON.stringify(codableCodesForRender ?? []));

      if (!sharedCodes) {
        const onlyShared = codes.filter(sharedCodePredicate);
        const firstIndex = codes.findIndex(sharedCodePredicate);
        const lastIndex = codes.findIndex(notSharedCodePredicate, firstIndex);
        const preArray = codes.slice(0, firstIndex);
        const postArray = codes.slice(firstIndex + onlyShared.length);

        const zippedShareCodes = {
          codes: onlyShared,
          codeType: commonEnums.CodeType.DIAGNOSIS,
          zipped: true
        };
        codes = [...preArray, zippedShareCodes, ...postArray];
      }

      const filtered = codes.filter(
        (code) =>
          cmxTypeguards.isChargeSessionCode(code) ||
          (cmxTypeguards.isZippedCodeBlock(code) && code.codes.length)
      );
      items = filtered.map(boundAccordionBuilderHandler);
      // TODO: Determine code types
      chargeMeta =
        invoice?.type !== commonEnums.InvoiceTypes.SHARED_CODES ? (
          <div className="charge-line">
            <span className="charge-index">Charge {lineNumber}:</span>
            <span className="charge-charge-type" style={{ display: 'none' }}>
              SOME CODE TYPE:
            </span>
            {editable ? (
              <span className="charge-delete-container">
                <a href="#/delete-session" onClick={this.onDelete}>
                  remove
                </a>
              </span>
            ) : (
              false
            )}
          </div>
        ) : (
          false
        );
    } else if (chargeSession) {
      items = chargeSession.codes.map(boundAccordionBuilderHandler) as any[];

      // TODO: Determine code types
      chargeMeta = (
        <div className="charge-line">
          <span className="charge-index">Charge {lineNumber}:</span>
          <span className="charge-charge-type" style={{ display: 'none' }}>
            SOME CODE TYPE:
          </span>
          {editable ? (
            <span className="charge-delete-container">
              <a href="#/delete-session" onClick={this.onDelete}>
                remove
              </a>
            </span>
          ) : (
            false
          )}
        </div>
      );
    } else if (transaction) {
      const diagnosis = [];
      const modifiers = [];
      // TODO:
      // const diagnosis = (invoice?.shared_codes.diagnoses ?? []).map(
      //   code => code.diagnosis_code
      // );
      // const modifiers = (invoice?.shared_codes.modifiers ?? []).map(
      //   code => code.modifier_code
      // );
      const dxString = diagnosis.length
        ? `diagnosis: ${diagnosis.join(', ')}`
        : '';
      const modString = modifiers.length
        ? `modifiers: ${modifiers.join(', ')}`
        : '';
      const conjunction = diagnosis.length && modifiers.length ? ' and ' : '';
      const alsoIncludes = `${dxString} ${conjunction} ${modString}`;
      styleClasses.push({
        'show-also-includes': diagnosis.length || modifiers.length
      });

      const label =
        txnType in CHARGE_TYPE_LABEL ? CHARGE_TYPE_LABEL[txnType] : txnType;

      items = transaction.codes.map(boundAccordionBuilderHandler) as any[];

      chargeMeta = (
        <div
          className={`transaction-meta transaction-${txnType.toLowerCase()}`}>
          <span className="ordinal">{lineNumber}. </span>
          {label}{' '}
          <span className="also-includes">Includes shared {alsoIncludes}</span>
        </div>
      );
      styleClasses.push({
        'charge-session-has-description': !!transaction.description
      });
    }

    return (
      <div className={classnames(styleClasses)}>
        {/* Charge Header Line */}
        {chargeMeta}

        {/* Transaction Error Message */}
        <ErrorSubmission
          errorMessages={codableTransaction?.errorMessages}
          messageStyle="txn-error-message"
          messageType="error"
        />

        {/* Procedure Error Message */}
        <ErrorSubmission
          errorMessages={codableTransaction?.procedure?.errorMessages}
          messageStyle="procedure-error-message"
          messageType="error"
        />

        {items.length ? (
          <Accordion
            onAction={this.onAccordionAction}
            dragManager={draggedCodesManager}
            canDropOnto={canDropOnto}
            onDropHandler={this.onDropHandler}
            selectable={true}
            droppable={true}
            items={items}
            errors={codableTransaction?.procedure?.errorMessages ?? []}
          />
        ) : editable ? (
          <div className="empty-message editable">
            Add codes using the search box below.
          </div>
        ) : (
          <div className="empty-message uneditable">No codes selected</div>
        )}

        {editable && codableTransaction && (
          <Lookup
            ref={this.lookupRef}
            actionPrefix=""
            placeholderText={
              invoice?.type === commonEnums.InvoiceTypes.SHARED_CODES
                ? 'Add shared diagnosis'
                : 'Search for code'
            }
            debounceTimeout={this.props.debounceTimeout}
            FieldDefinitions={codableTransaction.fieldDefinitions}
            shouldShowResults={shouldShowResults}
            autoFocus={autoFocus}
            onSearch={this.onSearch}
            onSearchSelection={this.onSearchSelection}
            searchResults={searchResults}
            searchResultsLookupKey={codableTransaction.guid}
            actionNameForDD={
              invoice?.type === commonEnums.InvoiceTypes.SHARED_CODES
                ? '~Focus code search (Shared)'
                : '~Focus code search (General)'
            }
          />
        )}
      </div>
    );
  }
}

export { Transaction };
